// Textos del mail del Radar Comercial — FUENTE ÚNICA:
// docs/decisiones/2026-09-27-radar-textos-mail-bloques.md (aprobado por Ignacio, reemplaza la
// versión anterior de este archivo). Asunto (bloque 0, siempre) + cuerpo en orden fijo: saludo,
// cordialidad, recuerdo del año pasado, impulso y valor, puente, paralelo (los dos lados, si ya se
// escribió al otro — CAMBIO 3, 2026-09-27), fotos,
// pregunta final, cierre ("Saludos," + primer nombre del vendedor) — la firma la agrega Gmail
// server-side (radar-borrador), después del cierre.
//
// Rotación: cada bloque con varias versiones elige una al azar en cada borrador (Math.random()),
// independiente de los demás — son puntos de partida, el vendedor edita en Gmail. No hay
// determinismo acá a propósito (a diferencia del resto del proyecto): "para que dos mails nunca
// salgan iguales" es el requisito, no un bug.
//
// Singular/plural según CANTIDAD DE DESTINATARIOS elegidos en el modal (no según cantidad de
// fotos ni de campañas): 1 = tuteo con su nombre; 2 = los dos nombres + "ustedes"; 3+ = "Hola a
// todos" + "ustedes".
//
// Perú: toda mención a Ipsos se reemplaza por "el estudio de Ipsos en nuestra red de Chile" (el
// estudio real se hizo en Chile — docs/decisiones/2026-09-27-ipsos-2025-datos-verificados.md,
// excepción aprobada por Ignacio a 10.281 acotada a citar el estudio con su origen explícito).
//
// Fotos como ADJUNTOS, nunca inline (decisión de Ignacio, 2026-09-27: "en el cuerpo rompen el
// mail") — el bloque de fotos es solo texto ("adjunto"/"adjuntamos", nunca "comparto"), sin
// marcador de inserción: las imágenes viajan como adjuntos del mail (`radar-borrador`), el cuerpo
// HTML no las referencia. Si `hayFotos` es false, el párrafo ni se agrega.

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

const primerNombre = (nombreCompleto) => (nombreCompleto || "").trim().split(/\s+/)[0] || "";

// ─── 0. Asunto (siempre) — docs/decisiones/2026-09-27-radar-textos-mail-bloques.md §0 ──────────
// "{Operación} & {Empresa} / {gancho}". {Operación}: el destinatario tiene que reconocer quién
// escribe en la bandeja de entrada. {Empresa} en formato título salvo siglas (palabras de hasta 4
// letras que YA vienen en mayúsculas en el dato de origen quedan igual: BCI, WOM, BCP, LG, LATAM;
// GILDEMEISTER -> Gildemeister).
const OPERACION_POR_PAIS = { Chile: "Wecast", Perú: "Visiona" };
const CIUDAD_POR_PAIS = { Chile: "Santiago", Perú: "Lima" };

const empresaTitulo = (empresa) =>
  (empresa || "")
    .trim()
    .split(/\s+/)
    .map((palabra) => {
      const esSigla = palabra.length > 0 && palabra.length <= 4 && palabra === palabra.toUpperCase();
      return esSigla ? palabra : palabra.charAt(0).toUpperCase() + palabra.slice(1).toLowerCase();
    })
    .join(" ");

// {personas} = personas de los edificios operativos del país, redondeado hacia abajo al millar
// (mismo cálculo que Inventario, ver `getConteoRedPorEstado`), formato `.toLocaleString("es-CL")`
// (convención del proyecto, ej. `conciliacionSII.js`/`rut.js`) — ej. Ignacio: "60.000 personas".
const formatoPersonasOperativas = (personas) => (Math.floor((personas || 0) / 1000) * 1000).toLocaleString("es-CL");

// Ganchos que rotan; el 5º solo entra al pool si hay un nombre de campaña REAL (no el fallback
// "una propuesta"/"una campaña" de bloque 3 — acá se recibe `campana` crudo).
const ganchoAsunto = ({ personasOperativos, operativos, ciudad, campana }) => {
  const personasTxt = formatoPersonasOperativas(personasOperativos);
  const ganchos = [
    `Conexión eficiente con más de ${personasTxt} personas ABC1 diariamente`,
    `Más de ${personasTxt} ejecutivos ABC1, todos los días, en ${operativos} edificios`,
    "Una audiencia ejecutiva identificada, empresa por empresa",
    `La audiencia ejecutiva más valiosa de ${ciudad}, todos los días`,
  ];
  if (campana) ganchos.push(`Nuevas ideas para ${campana} este año`);
  return pick(ganchos);
};

const bloqueAsunto = ({ empresa, pais, personasOperativos, operativos, campana }) => {
  const operacion = OPERACION_POR_PAIS[pais] || OPERACION_POR_PAIS.Chile;
  const ciudad = CIUDAD_POR_PAIS[pais] || CIUDAD_POR_PAIS.Chile;
  const gancho = ganchoAsunto({ personasOperativos, operativos, ciudad, campana });
  return `${operacion} & ${empresaTitulo(empresa)} / ${gancho}`;
};

// ─── Saludo por hora local del país del vendedor ───────────────────────────────────────────────
const TZ_POR_PAIS = { Chile: "America/Santiago", Perú: "America/Lima" };

const horaLocal = (pais) => {
  const tz = TZ_POR_PAIS[pais] || "America/Santiago";
  const parts = new Intl.DateTimeFormat("en-US", { hour: "2-digit", hour12: false, timeZone: tz }).formatToParts(
    new Date()
  );
  const h = parts.find((p) => p.type === "hour")?.value;
  return parseInt(h, 10) % 24;
};

const momentoDelDia = (pais) => (horaLocal(pais) < 12 ? "dia" : "tarde");

const bloqueSaludo = ({ nombres, momento }) => {
  const sufijo = momento === "dia" ? "buen día." : "buenas tardes.";
  if (!nombres || nombres.length === 0) return `Hola, ${sufijo}`;
  if (nombres.length === 1) return `Hola ${nombres[0]}, ${sufijo}`;
  if (nombres.length === 2) return `Hola ${nombres[0]} y ${nombres[1]}, ${sufijo}`;
  return `Hola a todos, ${sufijo}`;
};

// ─── 2. Cordialidad (rota, plural/singular) ────────────────────────────────────────────────────
const CORDIALIDAD = [
  {
    plural: "Un gusto saludarlos de vuelta; espero que estén muy bien.",
    singular: "Un gusto saludarte de vuelta; espero que estés muy bien.",
  },
  {
    plural: "Es un gusto volver a escribirles; espero que todo vaya muy bien.",
    singular: "Es un gusto volver a escribirte; espero que todo vaya muy bien.",
  },
  { plural: "¿Cómo están? Espero que muy bien.", singular: "¿Cómo estás? Espero que muy bien." },
  {
    plural: "Un gusto saludarlos; espero que estén teniendo una muy buena semana.",
    singular: "Un gusto saludarte; espero que estés teniendo una muy buena semana.",
  },
  {
    plural: "Espero que este mensaje los encuentre muy bien.",
    singular: "Espero que este mensaje te encuentre muy bien.",
  },
];

const bloqueCordialidad = (esSingular) => {
  const v = pick(CORDIALIDAD);
  return esSingular ? v.singular : v.plural;
};

// ─── 3. Recuerdo del año pasado (rota A/B, por lado × tipo) ────────────────────────────────────
const RECUERDO = {
  cliente: {
    propuso: [
      {
        plural: "En {mes} del año pasado trabajamos juntos la propuesta {campaña} y finalmente no se concretó.",
        singular: "En {mes} del año pasado trabajamos juntos la propuesta {campaña} y finalmente no se concretó.",
      },
      {
        plural: "Se viene {mes} y nos acordamos de {campaña}, la propuesta que revisamos juntos el año pasado.",
        singular: "Se viene {mes} y nos acordamos de {campaña}, la propuesta que revisamos juntos el año pasado.",
      },
    ],
    compro: [
      {
        plural: "En {mes} del año pasado hicimos juntos {campaña}, y fue un gusto acompañarlos.",
        singular: "En {mes} del año pasado hicimos juntos {campaña}, y fue un gusto acompañarte.",
      },
      {
        plural: "Se viene {mes} y nos acordamos de {campaña}, la campaña que hicimos juntos el año pasado.",
        singular: "Se viene {mes} y nos acordamos de {campaña}, la campaña que hicimos juntos el año pasado.",
      },
    ],
  },
  agencia: {
    propuso: [
      {
        plural: "En {mes} del año pasado les propusimos {campaña} para {empresa} y quedó fuera del plan.",
        singular: "En {mes} del año pasado te propusimos {campaña} para {empresa} y quedó fuera del plan.",
      },
      {
        plural:
          "Se acerca la ventana de {empresa}: el año pasado por esta fecha les propusimos {campaña} y quedó afuera. Esta vez no queremos que pase lo mismo.",
        singular:
          "Se acerca la ventana de {empresa}: el año pasado por esta fecha te propusimos {campaña} y quedó afuera. Esta vez no queremos que pase lo mismo.",
      },
    ],
    compro: [
      {
        plural: "En {mes} del año pasado salimos con {campaña} de {empresa}.",
        singular: "En {mes} del año pasado salimos con {campaña} de {empresa}.",
      },
      {
        plural: "Se acerca la ventana de {empresa}: el año pasado por esta fecha salimos juntos con {campaña}.",
        singular: "Se acerca la ventana de {empresa}: el año pasado por esta fecha salimos juntos con {campaña}.",
      },
    ],
  },
};

const reemplazar = (texto, vars) => texto.replace(/{([^{}]+)}/g, (_, k) => (vars[k] != null ? vars[k] : `{${k}}`));

const bloqueRecuerdo = ({ lado, tipo, esSingular, mes, campanaTxt, empresa }) => {
  const opciones = RECUERDO[lado]?.[tipo] || RECUERDO.cliente.propuso;
  const v = pick(opciones);
  const texto = esSingular ? v.singular : v.plural;
  return reemplazar(texto, { mes, campaña: campanaTxt, empresa });
};

// ─── 4. Impulso y valor (rota entre 5, con manejo de Perú/Ipsos y en_camino=0) ──────────────────
const fraseIpsos = (variante, pais) => {
  const esPeru = pais === "Perú";
  switch (variante) {
    case 1:
      return esPeru
        ? "con niveles de atención respaldados por el estudio de Ipsos en nuestra red de Chile"
        : "con niveles de atención respaldados por Ipsos";
    case 2:
      return esPeru
        ? "con una atención medida por el estudio de Ipsos en nuestra red de Chile"
        : "con una atención medida por Ipsos";
    case 3:
      return esPeru
        ? "la frecuencia y la atención no son una promesa: están respaldadas por el estudio de Ipsos en nuestra red de Chile"
        : "la frecuencia y la atención no son una promesa: están respaldadas por la investigación de Ipsos";
    case 4:
      return esPeru
        ? "con la atención respaldada por el estudio de Ipsos en nuestra red de Chile"
        : "con la atención respaldada por Ipsos";
    default:
      return "";
  }
};

const IMPULSO = [
  ({ operativos, enCamino, pais }) => {
    const camino = enCamino > 0 ? ` y sumando otros ${enCamino} entre instalación y piloto` : "";
    return `Este año seguimos avanzando: hoy estamos en ${operativos} edificios operativos${camino}, con cada vez más marcas y categorías que eligen el canal. Y lo esencial se mantiene: llegamos todos los días, de manera frecuente y eficiente, a una audiencia ejecutiva que conocemos con precisión, ${fraseIpsos(1, pais)}.`;
  },
  ({ operativos, enCamino, pais }) => {
    const camino =
      enCamino > 0 ? ` (hoy ${operativos} operativos y ${enCamino} más en camino)` : ` (hoy ${operativos} operativos)`;
    return `El canal sigue creciendo, no solo en edificios${camino} sino en contenido, en marcas y en categorías que confían en nosotros. Y con eso crece lo que nos distingue: una audiencia identificada edificio por edificio, empresa por empresa, a la que llegamos con frecuencia y ${fraseIpsos(2, pais)}.`;
  },
  ({ pais }) =>
    `Seguimos sumando: más edificios, más contenido y más marcas de primer nivel en pantalla. Es un canal B2B donde sabemos exactamente qué empresas hay en cada edificio, y donde ${fraseIpsos(3, pais)}.`,
  ({ operativos, enCamino, pais }) => {
    const camino = enCamino > 0 ? ` y ${enCamino} más entre instalación y piloto` : "";
    return `Este año el canal maduró: más marcas, más categorías y una red que sigue creciendo, hoy con ${operativos} edificios operativos${camino}. Nuestra audiencia está identificada, cuantificada y validada, y la alcanzamos todos los días con una frecuencia y una eficiencia que pocos medios pueden ofrecer, ${fraseIpsos(4, pais)}.`;
  },
  ({ operativos, enCamino, pais }) => {
    const esPeru = pais === "Perú";
    const inicio = esPeru
      ? "Nuestra audiencia nos respalda: según el estudio de Ipsos en nuestra red de Chile, el 98 % acepta el medio, el 86 % le presta atención y el 76 % recuerda las marcas."
      : "Nuestra audiencia nos respalda: según Ipsos, el 98 % acepta el medio, el 86 % le presta atención y el 76 % recuerda las marcas.";
    const camino = enCamino > 0 ? ` y ${enCamino} más en camino` : "";
    const cierre = ` Y el canal sigue creciendo, hoy con ${operativos} edificios operativos${camino}.`;
    const coda = esPeru
      ? " Y el comportamiento en un ascensor es el mismo en Santiago, en Lima o en cualquier ciudad del mundo."
      : "";
    return inicio + cierre + coda;
  },
];

const bloqueImpulso = ({ operativos, enCamino, pais }) => pick(IMPULSO)({ operativos, enCamino, pais });

// ─── 5. Puente (fijo por lado, singular solo en cliente) ───────────────────────────────────────
const bloquePuente = ({ lado, esSingular, empresa }) => {
  if (lado === "agencia") {
    return reemplazar(
      "Tenemos una propuesta más potente para {empresa} y nos gustaría que esté en la planificación de este año.",
      { empresa }
    );
  }
  const texto = esSingular
    ? "Creemos que podemos enriquecer el plan de {empresa} para este año y tenemos algunas ideas para compartirte."
    : "Creemos que podemos enriquecer el plan de {empresa} para este año y tenemos algunas ideas para compartirles.";
  return reemplazar(texto, { empresa });
};

// ─── 6. Paralelo (en los dos sentidos, 2026-09-27 — reemplaza la versión solo-cliente) ──────────
// Texto aprobado por Ignacio tras el caso Samsung: la frase salía en el mail al cliente con solo
// que la agencia TUVIERA contacto con email, aunque no se le hubiera escrito todavía. Ahora cada
// lado la recibe únicamente si YA se le escribió al OTRO lado (`armarMailRadar` recibe
// `yaEscribioOtroLado`, no más `hayAgenciaConEmail` — ver comentario de `armarMailRadar`).
// Mail al cliente: menciona la agencia, singular/plural según destinatarios del mail al cliente.
// Mail a la agencia: menciona la EMPRESA (formato título, 10.276/CAMBIO 2), mismo texto sin
// diferenciar singular/plural (texto aprobado, sin variante de tuteo).
const bloqueParalelo = ({ lado, esSingular, empresaTituloTxt }) => {
  if (lado === "agencia") {
    return reemplazar(
      "En paralelo le escribí al equipo de {empresa}; quedamos a disposición si quieren que hagamos una presentación juntos.",
      { empresa: empresaTituloTxt }
    );
  }
  return esSingular
    ? "En paralelo le escribí al equipo de tu agencia; quedamos a disposición si quieren que hagamos una presentación juntos."
    : "En paralelo le escribí al equipo de su agencia; quedamos a disposición si quieren que hagamos una presentación juntos.";
};

// ─── 7. Fotos (solo si hay al menos 1 foto, rota) ──────────────────────────────────────────────
const FOTOS = [
  {
    plural:
      "Aprovecho para adjuntarles algunas fotos de la actividad reciente en el canal; como verán, cada vez más marcas y categorías confían en nuestro medio.",
    singular:
      "Aprovecho para adjuntarte algunas fotos de la actividad reciente en el canal; como verás, cada vez más marcas y categorías confían en nuestro medio.",
  },
  {
    plural:
      "Les adjunto algunas fotos de lo que está pasando hoy en nuestras pantallas: cada vez más marcas de primer nivel confían en el canal.",
    singular:
      "Te adjunto algunas fotos de lo que está pasando hoy en nuestras pantallas: cada vez más marcas de primer nivel confían en el canal.",
  },
];

const bloqueFotos = (esSingular) => {
  const v = pick(FOTOS);
  return esSingular ? v.singular : v.plural;
};

// ─── 8. Pregunta final (rota) ───────────────────────────────────────────────────────────────────
const PREGUNTA_FINAL = [
  {
    plural: "¿Cuándo tendrían un espacio en su agenda para revisarlo juntos? Presencial o virtual, como prefieran.",
    singular: "¿Cuándo tendrías un espacio en tu agenda para revisarlo juntos? Presencial o virtual, como prefieras.",
  },
  {
    plural: "Me encantaría coordinar un momento para revisarlo juntos, presencial o virtual; ¿cuándo les queda cómodo?",
    singular:
      "Me encantaría coordinar un momento para revisarlo juntos, presencial o virtual; ¿cuándo te queda cómodo?",
  },
];

const bloquePreguntaFinal = (esSingular) => {
  const v = pick(PREGUNTA_FINAL);
  return esSingular ? v.singular : v.plural;
};

// ─── 9. Cierre (siempre, después de la pregunta y antes de la firma de Gmail) ──────────────────
// "Saludos," y en la línea siguiente el primer nombre del usuario logueado que crea el borrador
// (`usuarios.nombre`, nunca fijo) — un solo párrafo con salto de línea interno.
const bloqueCierre = (nombreVendedor) => `Saludos,<br />${primerNombre(nombreVendedor)}`;

/**
 * Arma asunto + cuerpo HTML del mail del Radar — FUENTE ÚNICA de textos:
 * docs/decisiones/2026-09-27-radar-textos-mail-bloques.md.
 *
 * @param {{
 *   lado:'cliente'|'agencia', tipo:'compro'|'propuso', empresa:string, campana:string|null,
 *   mes:string, pais:'Chile'|'Perú', nombresDestinatarios:string[], nombreVendedor:string,
 *   conteoRed:{operativos:number,instalacion:number,piloto:number,personasOperativos:number}|null,
 *   yaEscribioOtroLado?:boolean, hayFotos:boolean, fraseCategoriaTexto?:string
 * }} datos
 * @returns {{asunto:string, cuerpoHtml:string}}
 */
export const armarMailRadar = ({
  lado,
  tipo,
  empresa,
  campana,
  mes,
  pais,
  nombresDestinatarios,
  nombreVendedor,
  conteoRed,
  yaEscribioOtroLado,
  hayFotos,
  fraseCategoriaTexto,
}) => {
  const nombres = (nombresDestinatarios || []).map(primerNombre).filter(Boolean);
  const esSingular = nombres.length === 1;
  const campanaTxt = campana || (tipo === "propuso" ? "una propuesta" : "una campaña");
  const operativos = conteoRed?.operativos ?? 0;
  const enCamino = (conteoRed?.instalacion ?? 0) + (conteoRed?.piloto ?? 0);
  // CAMBIO 2 (2026-09-27, caso Samsung): {empresa} en formato título en TODO el cuerpo, misma
  // función que ya usaba el asunto (bloque 0) — nunca una copia local (10.1/10.27).
  const empresaTituloTxt = empresaTitulo(empresa);

  const parrafos = [
    bloqueSaludo({ nombres, momento: momentoDelDia(pais) }),
    bloqueCordialidad(esSingular),
    bloqueRecuerdo({ lado, tipo, esSingular, mes, campanaTxt, empresa: empresaTituloTxt }),
    bloqueImpulso({ operativos, enCamino, pais }),
    bloquePuente({ lado, esSingular, empresa: empresaTituloTxt }),
  ];

  // Fase 5 de /categorias (2026-10-07): párrafo propio con el dato Ipsos del rubro del cliente —
  // después del párrafo de la propuesta/campaña (bloquePuente), antes del llamado a la acción.
  // `fraseCategoriaTexto` ya viene armado por `fraseCategoria` (todo o nada: "" o undefined si al
  // cliente no se le puede calcular — nunca un placeholder ni un número suelto, 10.0/10.281).
  if (fraseCategoriaTexto) parrafos.push(fraseCategoriaTexto);

  // CAMBIO 3 (2026-09-27, caso Samsung): el paralelo ya no depende de si el otro lado TIENE
  // contacto con email, sino de si YA SE LE ESCRIBIÓ — en los dos sentidos (antes solo cliente).
  if (yaEscribioOtroLado) parrafos.push(bloqueParalelo({ lado, esSingular, empresaTituloTxt }));
  if (hayFotos) parrafos.push(bloqueFotos(esSingular));
  parrafos.push(bloquePreguntaFinal(esSingular), bloqueCierre(nombreVendedor));

  return {
    asunto: bloqueAsunto({
      empresa,
      pais,
      personasOperativos: conteoRed?.personasOperativos ?? 0,
      operativos,
      campana,
    }),
    cuerpoHtml: parrafos.map((p) => `<p>${p}</p>`).join(""),
  };
};
